import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Code,
  HStack,
  Input,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Bug, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { trpcAdmin } from '../lib/trpcAdmin';
import { useAuthAdmin } from '../AuthProviderAdmin';
import { FLAGS, isFlagKey, type FlagKey, useRefreshFlags, useFlag } from '@duke-hq/flags/react';
import { iconSize } from '@duke-hq/ui/constants/scalable';

function getFlagKeys(): FlagKey[] {
  return Object.keys(FLAGS).filter(isFlagKey);
}

function FlagDisplay({ flagKey }: { flagKey: FlagKey }) {
  const flagValue = useFlag(flagKey);

  return (
    <HStack
      w="full"
      p={3}
      bg="bg.surface"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      mb={2}
      spacing={4}
    >
      <Text fontSize="sm" fontFamily="mono" flex="1">
        {flagKey}
      </Text>
      <Code colorScheme={flagValue === true ? 'green' : 'red'} fontSize="sm" px={3} py={1}>
        {String(flagValue)}
      </Code>
    </HStack>
  );
}

export function FeatureFlagsPage() {
  const toast = useToast();
  const refetchFlags = useRefreshFlags();
  const [debugFlagKey, setDebugFlagKey] = useState('DEMO_FEATURE');
  const { user } = useAuthAdmin();
  const [debugUserDRN, setDebugUserDRN] = useState(user?.drn ?? '');

  const debugFlagMutation = trpcAdmin.flags.debugFlag.useMutation({
    onSuccess: (data) => {
      console.info('[feature-flags] === Flag Debug Results ===');
      console.info('[feature-flags] Full debug data:', JSON.stringify(data, null, 2));
      console.info('[feature-flags] === End Flag Debug ===');

      if ('error' in data) {
        toast({
          title: 'Debug Failed',
          description: String(data.error),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const mismatch = data.mismatch;
      toast({
        title: mismatch ? 'Mismatch Detected!' : 'No Mismatch',
        description: `Local: ${data.localEvaluation.flagValue}, Remote: ${data.remoteEvaluation.flagValue}. Check console for full details.`,
        status: mismatch ? 'warning' : 'success',
        duration: 8000,
        isClosable: true,
      });
    },
    onError: (error) => {
      console.error('[feature-flags] Debug failed:', error);
      toast({
        title: 'Debug Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const refreshMutation = trpcAdmin.flags.refreshAll.useMutation({
    onSuccess: (data) => {
      const description = `Notified ${data.containersNotified} container(s). Check browser console for details.`;

      // Trigger frontend to refetch flags from backend
      // This ensures the frontend picks up any changes to flags.env in local dev
      refetchFlags();

      toast({
        title: 'Flags Refreshed',
        description,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
    },
    onError: (error) => {
      console.error('[feature-flags] Refresh failed:', error);
      toast({
        title: 'Refresh Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const debug = refreshMutation.data?.debug;
  const isUsingLocalFlags =
    debug?.environment?.startsWith('local') && debug?.forcePosthogLocal !== 'true';

  return (
    <Box p="4">
      <VStack spacing={6} align="stretch">
        <PageHeading>Feature Flags</PageHeading>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>About Feature Flag Caching</AlertTitle>
            <AlertDescription>
              Feature flags are cached on each server container for performance. Changes made in
              PostHog may take up to 15 minutes to propagate automatically. Use the button below to
              force an immediate refresh across all containers and reload the frontend flags cache.
              In local dev, this will also pick up any changes to your <Code>flags.env</Code> file.
            </AlertDescription>
          </Box>
        </Alert>

        <Card>
          <CardHeader>
            <Text textStyle="lg" fontWeight="semibold">
              Cache Management
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="start">
              <Text>
                Click the button below to refresh feature flag definition caches across all running
                server containers. This will fetch the latest flag values from PostHog (or{' '}
                <Code>flags.env</Code> in local dev) and update the frontend immediately.
              </Text>

              <Button
                leftIcon={<RefreshCw size={iconSize.md} />}
                onClick={() => refreshMutation.mutate()}
                isLoading={refreshMutation.isPending}
                loadingText="Refreshing..."
                colorScheme="blue"
              >
                Refresh Feature Flags (All Containers)
              </Button>

              {refreshMutation.data ? (
                <>
                  <SimpleGrid columns={2} spacing={4} w="full" mt={4}>
                    <Stat>
                      <StatLabel>Containers Notified</StatLabel>
                      <StatNumber>{refreshMutation.data.containersNotified}</StatNumber>
                      <StatHelpText>Active subscribers</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Last Refresh</StatLabel>
                      <StatNumber>
                        {new Date(refreshMutation.data.timestamp).toLocaleTimeString()}
                      </StatNumber>
                      <StatHelpText>
                        {new Date(refreshMutation.data.timestamp).toLocaleDateString()}
                      </StatHelpText>
                    </Stat>
                  </SimpleGrid>

                  {isUsingLocalFlags ? (
                    <Alert status="warning" borderRadius="md" mt={4}>
                      <AlertIcon />
                      <Box>
                        <AlertTitle>Using Local Flags (flags.env)</AlertTitle>
                        <AlertDescription>
                          Environment is <Code>{debug?.environment}</Code> and{' '}
                          <Code>FORCE_POSTHOG_LOCAL</Code> is not set to &quot;true&quot;. Flags are
                          being read from the local <Code>flags.env</Code> file, NOT from PostHog.
                          To use feature flags from remote PostHog in your local development, set{' '}
                          <Code>FORCE_POSTHOG_LOCAL=true</Code> and make sure you have{' '}
                          <Code>PLATFORM_POSTHOG_API_KEY</Code>,{' '}
                          <Code>PLATFORM_POSTHOG_SECRET_KEY</Code> and{' '}
                          <Code>PLATFORM_POSTHOG_HOST</Code> variables set too.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  ) : null}

                  <Alert status="success" borderRadius="md" mt={4}>
                    <AlertIcon />
                    <Box w="full">
                      <AlertTitle>Debug Info</AlertTitle>
                      <AlertDescription>
                        <VStack align="start" spacing={1} mt={2}>
                          <Text>
                            <strong>User:</strong> {refreshMutation.data.userId}
                          </Text>
                          <Text>
                            <strong>Environment:</strong> {debug?.environment}
                          </Text>
                          <Text>
                            <strong>FORCE_POSTHOG_LOCAL:</strong>{' '}
                            {debug?.forcePosthogLocal ?? 'not set'}
                          </Text>
                          <Text>
                            <strong>Has Personal API Key (PLATFORM_POSTHOG_SECRET_KEY):</strong>{' '}
                            {debug?.clientInfo?.hasPersonalApiKey ? 'Yes' : 'No'}
                          </Text>
                          <Text>
                            <strong>Using Local Flags (flags.env):</strong>{' '}
                            {debug?.clientInfo?.usingLocalFlags ? 'Yes' : 'No'}
                          </Text>
                        </VStack>
                      </AlertDescription>
                    </Box>
                  </Alert>

                  <Alert status="info" borderRadius="md" mt={4}>
                    <AlertIcon />
                    <Box w="full">
                      <AlertTitle>Your Feature Flags</AlertTitle>
                      <AlertDescription>
                        <VStack align="start" spacing={0} mt={2} w="full">
                          {getFlagKeys()
                            .sort((a, b) => a.localeCompare(b))
                            .map((flagKey) => (
                              <FlagDisplay key={flagKey} flagKey={flagKey} />
                            ))}
                          {getFlagKeys().length === 0 && (
                            <Text fontSize="sm" color="gray.500">
                              No feature flags found
                            </Text>
                          )}
                        </VStack>
                      </AlertDescription>
                    </Box>
                  </Alert>
                </>
              ) : null}
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <Text textStyle="lg" fontWeight="semibold">
              Debug Flag Evaluation
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="start">
              <Text>
                Compare local vs remote flag evaluation for a specific flag and user. This helps
                diagnose why local evaluation might differ from remote.
              </Text>

              <VStack spacing={2} align="start" w="full">
                <Text fontWeight="medium">Flag Key:</Text>
                <Input
                  value={debugFlagKey}
                  onChange={(e) => setDebugFlagKey(e.target.value)}
                  placeholder="e.g., DEMO_FEATURE"
                />
              </VStack>

              <VStack spacing={2} align="start" w="full">
                <Text fontWeight="medium">User DRN:</Text>
                <Input
                  value={debugUserDRN}
                  onChange={(e) => setDebugUserDRN(e.target.value)}
                  placeholder="The user's DRN"
                />
              </VStack>

              <Button
                leftIcon={<Bug size={iconSize.md} />}
                onClick={() =>
                  debugFlagMutation.mutate({ flagKey: debugFlagKey, userDRN: debugUserDRN })
                }
                isLoading={debugFlagMutation.isPending}
                loadingText="Debugging..."
                colorScheme="orange"
              >
                Debug Flag Evaluation
              </Button>

              {debugFlagMutation.data && !('error' in debugFlagMutation.data) ? (
                <Alert
                  status={debugFlagMutation.data.mismatch ? 'error' : 'success'}
                  borderRadius="md"
                  mt={4}
                >
                  <AlertIcon />
                  <Box>
                    <AlertTitle>
                      {debugFlagMutation.data.mismatch
                        ? 'Local vs Remote Mismatch!'
                        : 'Local and Remote Match'}
                    </AlertTitle>
                    <AlertDescription>
                      <VStack align="start" spacing={2} mt={2}>
                        <HStack>
                          <Text fontWeight="bold">Local Evaluation:</Text>
                          <Code
                            colorScheme={
                              debugFlagMutation.data.localEvaluation.flagValue ? 'green' : 'red'
                            }
                          >
                            {String(debugFlagMutation.data.localEvaluation.flagValue)}
                          </Code>
                        </HStack>
                        <HStack>
                          <Text fontWeight="bold">Remote Evaluation:</Text>
                          <Code
                            colorScheme={
                              debugFlagMutation.data.remoteEvaluation.flagValue ? 'green' : 'red'
                            }
                          >
                            {String(debugFlagMutation.data.remoteEvaluation.flagValue)}
                          </Code>
                        </HStack>
                        <Text fontSize="sm" color="gray.600" mt={2}>
                          Full debug data logged to browser console (F12)
                        </Text>
                      </VStack>
                    </AlertDescription>
                  </Box>
                </Alert>
              ) : null}
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
}
